import {database, firestore} from '@src/db';
import { ref, runTransaction } from 'firebase/database';

/**
 * O contador tem que ser uma FOLHA. Objeto aqui significa que o `id` apontou para um nó PAI —
 * `sequential/{companyId}` em vez de `sequential/{companyId}/financialItems`. Coagir para 0 e
 * gravar apagaria todos os contadores irmãos da empresa (guia, lote, numMov, repasses…): foi
 * exatamente o #1230, pela importação de OFX. Quem detecta ABORTA a transação (updater devolve
 * `undefined`, nada é escrito) e lança um erro que diz o caminho certo.
 */
export function isParentNode(value) {
  return !!value && typeof value === 'object';
}

export function parentNodeError(id) {
  return new Error(`Sequential: 'sequential/${id}' é um nó com filhos (contadores irmãos), não um contador — o id precisa apontar para o contador, ex.: '{companyId}/financialItems' (#1230)`);
}

export const MAX_TRIES = 100;

/**
 * Esgotaram-se as tentativas sem achar código livre. Antes isto virava `return 0`, e o caller
 * gravava `code: 0` sem perceber — foram as cobranças com código 0 (#1243). Quem chama decide o
 * que fazer; ninguém deve persistir um código a partir deste erro.
 */
export class SequentialExhaustedError extends Error {
  constructor(query, tries) {
    super(`Sequential: ${tries} tentativas em 'sequential/${query?.id}' sem código livre em ${query?.table}.${query?.field} — o contador está atrás da coleção (#1243)`);
    this.name = 'SequentialExhaustedError';
    this.query = query;
    this.tries = tries;
  }
}

/**
 * Fronteira da coleção para o contador pular quando colide: a QUANTIDADE de documentos que
 * passam nos mesmos filtros da conferência. Com numeração contígua a partir de 1, é o maior
 * código; com códigos zerados/duplicados fica acima (sobra uma lacuna, sem dano); com lacunas na
 * numeração fica abaixo e o laço segue conferindo um a um a partir dali.
 *
 * Uma agregação em vez de 100 round-trips. Não dá para pedir o "maior código" direto: `code` é
 * string, e `orderBy('code','desc')` é lexicográfico ('999' > '1000'). O auto-fix antigo, na
 * 10ª tentativa, nem isso fazia: mantinha o `where(code == count)` e devolvia o próprio documento
 * que tinha colidido, então nunca avançava.
 *
 * Só vale quando TODO documento filtrado tem o campo, e com um número da PRÓPRIA sequência:
 * documento sem o campo e número digitado fora dela inflam a contagem do mesmo jeito, e o salto
 * cai depois do fim real da sequência. Passam `frontier: false` (o laço anda de 1 em 1):
 * - número de guia (`events`): a contagem é de todos os eventos ativos, não dos que têm guia;
 * - `appeals.sequencialTransacao`: campo digitável — em produção (09/2026) 4 de 21 empresas têm
 *   números fora da sequência, e o salto deixaria buraco de até 119 num número do XML TISS.
 */
async function collectionFrontier(_ref) {
  return parseInt(await _ref.fcmCount(), 10) || 0;
}

export async function getNewSequential(query) {
  const {id, table, field, isNumber} = Object.assign({}, query);
  if(!id) {
    return 0;
  }

  let _ref = null;
  if(table && field) {
    _ref = firestore.collection(table);
    if(query.active===true || query.active===false) {
      _ref = _ref.where('active', '==', query.active);
    }
    if(query.companyId) {
      _ref = _ref.where('companyId', '==', query.companyId);
    }
    if(query.matrizId) {
      _ref = _ref.where('matrizId', '==', query.matrizId);
    }
  }

  function fixType(value) {
    if(isNumber) {
      return value;
    }
    return String(value);
  }

  let count = 0;
  let countFix = 0;
  let jumped = false;
  let trys = 0;
  let parentNode = false;
  while((trys++)<MAX_TRIES) {
    await runTransaction(ref(database, 'sequential/' + id), (countField) => {
      if(isParentNode(countField)) {
        parentNode = true;
        return undefined; // aborta: nada é gravado
      }
      // Garantir que countField sempre é um número válido (previne NaN)
      if(!countField || isNaN(countField) || typeof countField !== 'number') {
        countField = 0;
      }
      // O updater precisa ser IDEMPOTENTE: o SDK o reexecuta quando o valor local difere do
      // servidor (caminho nunca lido começa em null). Zerar `countFix` aqui dentro perdia o salto
      // na reexecução — o contador voltava a andar de 1 em 1 e esgotava do mesmo jeito.
      if(countFix>countField) {
        countField = countFix;
      }
      countField++;
      count = countField;
      return countField;
    });
    if(parentNode) {
      throw parentNodeError(id);
    }
    countFix = 0;


    if(table && field) {
      const existsObj = await _ref.where(field, '==', fixType(count)).limit(1).get();
      if(existsObj.empty) {
        return fixType(count);
      }
      // Colidiu: o contador está atrás da coleção. Uma vez, pula para a fronteira; depois
      // segue um a um (cobre lacunas). Sem isto eram 100 colisões e `return 0` (#1243).
      if(!jumped && query.frontier !== false) {
        jumped = true;
        countFix = await collectionFrontier(_ref);
        console.warn('Sequential: contador atrás da coleção, pulando para a fronteira', {...query, count, frontier: countFix});
      }
    }
    else {
      return fixType(count);
    }
  }
  console.error('Sequential exauts trys', query);
  throw new SequentialExhaustedError(query, MAX_TRIES);
}

export async function getNewSequentialMulti(query) {
  const {id, table, field, isNumber, qtd} = Object.assign({}, query);
  if(!id) {
    return 0;
  }
  if(!(qtd>0)) {
    return [];
  }

  function fixTypeOut(value) {
    return isNumber?value:String(value);
  }

  // Caminho rápido: SEM validação contra coleção (`table`/`field` vazios), a sequência é um
  // contador puro — dá para reservar o bloco inteiro numa ÚNICA transação do RTDB, em vez de
  // uma transação por código.
  //
  // Isso era o passo mais caro do fechamento de repasse: cada `runTransaction` é um round-trip
  // com confirmação do servidor (~450ms medidos em produção), e o laço abaixo fazia um por
  // pagamento — 51 pagamentos custavam ~23s só para numerar as movimentações. Reservar o bloco
  // é igualmente seguro contra concorrência: a transação é atômica, então dois fechamentos
  // simultâneos recebem faixas disjuntas.
  if(!table || !field) {
    // O fim da faixa sai do RESULTADO da transação (`snapshot.val()` do commit), não de uma
    // variável capturada pelo closure: a callback pode rodar várias vezes (retry otimista) e,
    // se algum dia deixasse de retornar valor, a transação abortaria com `committed: false` e o
    // closure entregaria 0 — o laço abaixo geraria códigos ≤ 0 em silêncio. Hoje ela sempre
    // retorna, então isto é só a validação que fecha a porta.
    let parentNode = false;
    const result = await runTransaction(ref(database, 'sequential/' + id), (countField) => {
      if(isParentNode(countField)) {
        parentNode = true;
        return undefined; // aborta: nada é gravado
      }
      if(!countField || isNaN(countField) || typeof countField !== 'number') {
        countField = 0;
      }
      return countField + qtd;
    });
    if(parentNode) {
      throw parentNodeError(id);
    }
    const end = result?.snapshot?.val();
    if(!result?.committed || typeof end !== 'number' || !(end >= qtd)) {
      console.error('getNewSequentialMulti: reserva do bloco não confirmada', {id, qtd, committed: result?.committed, end});
      throw new Error('Falha ao reservar o bloco de sequência');
    }
    const codes = [];
    for(let i = end - qtd + 1; i <= end; i++) {
      codes.push(fixTypeOut(i));
    }
    return codes;
  }

  // Caminho COM validação contra a coleção: cada código precisa ser conferido individualmente
  // (pode já existir), então segue um por vez.
  let _ref = firestore.collection(table);
  if(query.active===true || query.active===false) {
    _ref = _ref.where('active', '==', query.active);
  }
  if(query.companyId) {
    _ref = _ref.where('companyId', '==', query.companyId);
  }
  if(query.matrizId) {
    _ref = _ref.where('matrizId', '==', query.matrizId);
  }

  const codes = [];

  let count = 0;
  let countFix = 0;
  let jumped = false;
  let trys = 0;
  let parentNode = false;
  while((trys++)<MAX_TRIES) {
    await runTransaction(ref(database, 'sequential/' + id), (countField) => {
      if(isParentNode(countField)) {
        parentNode = true;
        return undefined; // aborta: nada é gravado
      }
      // Garantir que countField sempre é um número válido (previne NaN)
      if(!countField || isNaN(countField) || typeof countField !== 'number') {
        countField = 0;
      }
      // O updater precisa ser IDEMPOTENTE: o SDK o reexecuta quando o valor local difere do
      // servidor (caminho nunca lido começa em null). Zerar `countFix` aqui dentro perdia o salto
      // na reexecução — o contador voltava a andar de 1 em 1 e esgotava do mesmo jeito.
      if(countFix>countField) {
        countField = countFix;
      }
      countField++;
      count = countField;
      return countField;
    });
    if(parentNode) {
      throw parentNodeError(id);
    }
    countFix = 0;

    const existsObj = await _ref.where(field, '==', fixTypeOut(count)).limit(1).get();
    if(existsObj.empty) {
      codes.push(fixTypeOut(count));
      trys = 0;
      if (codes?.length>=qtd) {
        return codes;
      }
    }
    else if(!jumped && query.frontier !== false) {
      // Mesma fronteira do getNewSequential: uma agregação, não 100 colisões.
      jumped = true;
      countFix = await collectionFrontier(_ref);
      console.warn('Sequential: contador atrás da coleção, pulando para a fronteira', {...query, count, frontier: countFix});
    }
  }
  console.error('Sequential exauts trys', query);
  throw new SequentialExhaustedError(query, MAX_TRIES);
}

